import round from 'lodash/round';
import set from 'lodash/set';
import compact from 'lodash/compact';
import padStart from 'lodash/padStart';
import upperFirst from 'lodash/upperFirst';
import cn from 'classnames';
import moment from 'moment';


export function detectPlatform(){
  const userAgentKeywords = { // Do not change order of keywords
    'iPhone': 'ios',
    'iPad': 'ios',
    'iPod': 'ios',
    'android': 'android',
    'Win': 'windows',
    'Mac': 'mac',
  };
  for (let keyword in userAgentKeywords) {
    if (navigator.userAgent.indexOf(keyword) > -1) {
      return userAgentKeywords[keyword];
    }
  }
  return '';
}


export function detectBrowser() {
  const userAgentKeywords = { // Do not change order of keywords
    'Opera': 'opera',
    'Chrome': 'chrome',
    'Edge': 'edge',
    'Safari': 'safari',
    'Firefox': 'firefox',
    'MSIE': 'ie',
  };
  for (let keyword in userAgentKeywords) {
    if (navigator.userAgent.indexOf(keyword) > -1) {
      return userAgentKeywords[keyword];
    }
  }
  return '';
}


export function isMobileSizeScreen(){
  return window.innerWidth < 768; // equals to $grid-breakpoint-md from src/styles/variables.scss
}


export function getFromLocalStorage(key, defaultValue){
  try {
    const data = JSON.parse(localStorage.getItem(key));
    return data === null ? defaultValue : data;
  }
  catch (e) {
    return defaultValue || null;
  }
}


export function setToLocalStorage(key, data){
  localStorage.setItem(key, JSON.stringify(data));
}


export function parseGETParams(GETParamsString){
  const params = {};
  if (!GETParamsString.trim()) {
    return {};
  }
  GETParamsString
    .replace(/^\?/, '')
    .split('&')
    .forEach(param => {
      const [ key, value ] = param.split('=');
      let serializedValue = decodeURIComponent(value);
      try {
        serializedValue = JSON.parse(serializedValue);
      }
      catch (e) {}
      params[decodeURIComponent(key)] = serializedValue;
    });
  return params;
}


export function parseDocumentTitle(){
  const base = document.title.replace(/^\(([0-9]+)\)\s*/, '');
  const counter = +document.title.replace(/^(?:\(([0-9]+)\))?.*/, '$1') || 0;
  return { base, counter };
}


export function exposeToGlobalScope(key, value){
  const path = '__ELIXIRCHAT__.' + key;
  set(window, path, value);
}


export function exposeComponentToGlobalScope(instance){
  exposeToGlobalScope(instance.constructor.name, instance);
}


export function isWebImage(mimeType){
  return ['image/jpg', 'image/jpeg', 'image/png', 'image/gif'].includes(mimeType.toLowerCase().trim());
}


export function isWebVideo(mimeType){
  return mimeType.toLowerCase().trim() === 'video/mp4';
}


export function fitDimensionsIntoLimits(originalWidth, originalHeight, limitWidth, limitHeight){
  limitWidth = limitWidth || Infinity;
  limitHeight = limitHeight || Infinity;
  const originalRatio = originalWidth / originalHeight;
  const newWidth = Math.min(originalWidth, limitWidth, limitHeight * originalRatio);
  const newHeight = newWidth / originalRatio;
  return [round(newWidth), round(newHeight)];
}


/**
 * Adds "prev" prefix to each prop for better code readability (e.g. currentRoom becomes prevCurrentRoom)
 */
export function prefixPrevProps(prevProps){
  const prefixedPrevProps = {};
  for (let key in prevProps) {
    const prefixedKey = 'prev' + upperFirst(key);
    prefixedPrevProps[prefixedKey] = prevProps[key];
  }
  return prefixedPrevProps;
}


export function applyQuickAnimation(element, animationClassName, timeout = 1000){
  if (element instanceof HTMLElement) {
    element.classList.add(animationClassName);
    setTimeout(() => {
      element.classList.remove(animationClassName);
    }, timeout);
  }
}


export function shakeElement(element){
  applyQuickAnimation(element, 'globalShakeElement');
}


export function flashElement(element){
  applyQuickAnimation(element, 'globalFlashElement', 2000);
}


export function scrollToElement(element, callback, options){
  if (element instanceof HTMLElement) {
    const { behavior = 'smooth', block = 'center', inline = 'nearest' } = options || {};
    const intersectionObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        intersectionObserver.unobserve(element);
        callback && callback();
      }
    });
    intersectionObserver.observe(element);
    element.scrollIntoView({ behavior, block, inline });
  }
}


export function validateEmail(email){
  const emailRegex = /^(([^<>()\[\].,;:\s@"]+(\.[^<>()\[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
  return emailRegex.test(email);
}


export function inflect(number, endings, hideNumber = false){
  const cases = [2, 0, 1, 1, 1, 2];
  const endingIndex = (number % 100 > 4 && number % 100 < 20) ? 2 : cases[ Math.min(number % 10, 5) ];
  const ending = endings[endingIndex] || endings[0];
  return hideNumber ? ending : number + ' ' + ending;
}


export function getHumanReadableFileSize(sizeInBytes){
  const unitsDict = {
    'kb': 'Кб',
    'mb': 'Мб',
    'gb': 'Гб',
  };
  const sizeInKb = sizeInBytes / 1024;
  const sizeInMb = sizeInKb / 1024;
  const sizeInGb = sizeInMb / 1024;

  let primarySize = sizeInKb;
  let primaryUnit = 'kb';
  if (sizeInGb > 1) {
    primarySize = sizeInGb;
    primaryUnit = 'gb';
  }
  else if (sizeInMb > 1) {
    primarySize = sizeInMb;
    primaryUnit = 'mb';
  }
  primarySize = primarySize < 0.1 ? 0.1 : +(primarySize.toFixed(1));
  return primarySize.toLocaleString('ru-RU') + ' ' + unitsDict[primaryUnit];
}


export function formatNumber(number, separator = ' '){
  return !isNaN(number)
    ? (+number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator)
    : '';
}


export function stringifyTimePeriod(durationMs, options) {
  const {
    format = 'string',
    isCompact = false,
    includeSeconds = false,
    padHours = false,
    customDict,
  } = options || {};

  const totalSeconds = Math.floor(durationMs / 1000);
  const totalMinutes = Math.floor(durationMs / 1000 / 60);
  const totalHours = Math.floor(durationMs / 1000 / 60 / 60);
  const totalDays = Math.floor(durationMs / 1000 / 60 / 60 / 24);

  const displayedHours = totalHours - totalDays * 24;
  const displayedMinutes = totalMinutes - totalHours * 60;
  const displayedSeconds = totalSeconds - totalMinutes * 60;

  if (format === 'numeric') {
    return compact([
      padHours ? padStart(totalHours.toString(), 2, '0') : totalHours,
      padStart(displayedMinutes.toString(), 2, '0'),
      padStart(displayedSeconds.toString(), 2, '0')
    ]).join(':');
  }
  else if (format === 'string') {
    const dictRegular = {
      lessThanMinute: 'меньше минуты',
      days: ['день', 'дня', 'дней'],
      hours: ['час', 'часа', 'часов'],
      minutes: ['минута', 'минуты', 'минут'],
      seconds: ['секунда', 'секунды', 'секунд'],
    };
    const dictCompact = {
      lessThanMinute: '1 м',
      days: ['д'],
      hours: ['ч'],
      minutes: ['м'],
      seconds: ['с'],
    };
    let dict = isCompact ? dictCompact : dictRegular;
    if (customDict) {
      dict = customDict;
    }
    if (!includeSeconds && totalMinutes < 1) {
      return dict.lessThanMinute;
    }
    const periodArr = [];
    if (totalDays > 0 && dict.days) {
      periodArr.push(inflect(totalDays, dict.days));
    }
    if (displayedHours > 0 && dict.hours) {
      periodArr.push(inflect(displayedHours, dict.hours));
    }
    if (displayedMinutes > 0 && dict.minutes) {
      periodArr.push(inflect(displayedMinutes, dict.minutes));
    }
    if (includeSeconds && displayedSeconds > 0 && dict.seconds) {
      periodArr.push(inflect(displayedSeconds, dict.seconds));
    }
    return periodArr.join(' ');
  }
}


export function shortenFileName(filename, nameLength = 15){
  const [ name, extension ] = filename.split(/\.(?=[^\.]+$)/);
  if (name.length > nameLength) {
    const fileNameBeginningLength = Math.ceil(nameLength / 2);
    const fileNameEndingLength = Math.floor(nameLength / 2);
    return name.substr(0, fileNameBeginningLength)
      + '…'
      + name.substr(name.length - fileNameEndingLength)
      + '.'
      + extension;
  }
  else {
    return filename;
  }
}


// TODO: move to backend
export function getSoundexHash(word = ''){
  const rules = [
    [/[aehiouy]/g, ''],
    [/[йуеёыахоэяиюьъ]/g, ''],
    [/[с]?тч/g, 'щ'],
    [/rl/g, 'r'],
    [/[bfpvw]/g, '1'],
    [/[бфпв]/g, '1'],
    [/[cgjkqsxz]/g, '2'],
    [/[цжкзсг]/g, '2'],
    [/[dt]/g, '3'],
    [/[дтщшч]/g, '3'],
    [/[l]/g, '4'],
    [/[л]/g, '4'],
    [/[mn]/g, '5'],
    [/[мн]/g, '5'],
    [/[r]/g, '6'],
    [/[р]/g, '6'],
    [/([0-9])(\1{1,})/g, '$1']
  ];
  let result = word.trim().toLowerCase();
  rules.forEach((rule) => {
    result = result.replace(rule[0], rule[1])
  });
  return result;
}


export function mergeClassNames(classNames, additionalClassNames){
  const mergedClassNames = { ...classNames };
  if (classNames && additionalClassNames) {
    for (let classNameKey in additionalClassNames) {
      if (classNames[classNameKey]) {
        mergedClassNames[classNameKey] = cn(classNames[classNameKey], additionalClassNames[classNameKey]);
      }
    }
  }
  return mergedClassNames;
}


export function objectDiffShallow(originalObject, objectToCompare){
  const diff = {};
  const keys = Object.keys({ ...originalObject, ...objectToCompare });
  keys.forEach(key => {
    if (originalObject[key] !== objectToCompare[key]) {
      diff[key] = objectToCompare[key];
    }
  });
  return diff;
}


export function withMomentLocale(customLocale, callback){
  const savedLocale = moment.locale();
  moment.locale(customLocale);
  const result = callback();
  moment.locale(savedLocale);
  return result;
}


export function isWithinElement(target, container){
  if (typeof container === 'string') {
    return Boolean(target.closest('.' + container));
  }
  else if (container instanceof HTMLElement) {
    return container.contains(target) || target === container;
  }
  return false;
}


export function defaultsTo(data, defaultValues){
  const serializedData = {};
  for (let key in defaultValues) {
    serializedData[key] = data?.[key] || defaultValues[key];
  }
  return serializedData;
}


export function normalizeErrorStack(errorStackString, maxLines) {
  return (errorStackString || '')
    .trim()
    .replace(/^Error\n\s*/, '')
    .split(/\n/)
    .map(row => row.trim())
    .filter(row => row)
    .slice(0, maxLines || undefined)
    .join('\n');
}


// Simple non-secure hash function (implementation of Java's String.hashCode() method)
export function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    let char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash;
}
